import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { Users, MessageSquare, Heart, Send, Edit2, Trash2, Calendar } from 'lucide-react';
import Card from '../components/Card';
import Button from '../components/Button';
import PostCard from '../components/PostCard';
import EventCard from '../components/EventCard';
import PostDetailsModal from '../components/PostDetailsModal';
import InformationHubList from '../components/InformationHub/InformationHubList';
import supabase from "../lib/supabase";
import { format } from 'date-fns';
import { useNavigate, useLocation } from 'react-router-dom';

function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const [posts, setPosts] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPost, setSelectedPost] = useState<any | null>(null);
  const [activeView, setActiveView] = useState<'community' | 'info'>('community');
  const [feedType, setFeedType] = useState<'posts' | 'events'>('posts');
  const [infoHubPosts, setInfoHubPosts] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [hasCommunities, setHasCommunities] = useState(false);

  useEffect(() => {
    // Check for view in location state
    if (location.state?.view) {
      setActiveView(location.state.view);
    }
    fetchFeed();
  }, [user, location.state]);

  const fetchPosts = async (communityIds: string[]) => {
    try {
      const { data: postsData, error: postsError } = await supabase
        .from('community_posts')
        .select(`
          *,
          author:author_id(
            id,
            full_name,
            display_name,
            verified,
            avatar_url
          ),
          category:category_id(
            id,
            name
          ),
          community:community_id(
            id,
            name
          ),
          likes
        `)
        .in('community_id', communityIds)
        .is('parent_id', null)
        .order('created_at', { ascending: false })
        .limit(20);

      if (postsError) throw postsError;
      setPosts(postsData || []);
    } catch (err) {
      console.error('Error fetching posts:', err);
      throw err;
    }
  };

  const fetchEvents = async (communityIds: string[]) => {
    try {
      const { data: eventsData, error: eventsError } = await supabase
        .from('community_events')
        .select(`
          *,
          creator:creator_id(
            id,
            full_name,
            display_name,
            verified,
            avatar_url
          ),
          community:community_id(
            id,
            name
          ),
          rsvps:event_rsvps(count)
        `)
        .in('community_id', communityIds)
        .gte('start_time', new Date().toISOString())
        .order('start_time', { ascending: true });

      if (eventsError) throw eventsError;
      setEvents(eventsData || []);
    } catch (err) {
      console.error('Error fetching events:', err);
      throw err;
    }
  };

  const fetchFeed = async () => {
    if (!user) return;

    try {
      setIsLoading(true);
      setError(null);

      const { data: memberships, error: membershipError } = await supabase
        .from('community_members')
        .select('community_id')
        .eq('user_id', user.id);

      if (membershipError) throw membershipError;

      const communityIds = memberships?.map(m => m.community_id) || [];
      setHasCommunities(communityIds.length > 0);

      if (communityIds.length > 0) {
        await Promise.all([
          fetchPosts(communityIds),
          fetchEvents(communityIds)
        ]);
      }

      await Promise.all([
        fetchInfoHubPosts(),
        fetchCategories()
      ]);
    } catch (err: any) {
      console.error('Error fetching feed:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePostUpdate = async () => {
    if (!user) return;

    try {
      const { data: memberships } = await supabase
        .from('community_members')
        .select('community_id')
        .eq('user_id', user.id);

      const communityIds = memberships?.map(m => m.community_id) || [];
      if (communityIds.length > 0) {
        await fetchPosts(communityIds);
        await fetchEvents(communityIds);
      }
    } catch (err) {
      console.error('Error updating posts:', err);
    }
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedCategories(value === 'all' ? [] : [value]);
  };

  useEffect(() => {
    if (activeView === 'info') {
      fetchInfoHubPosts();
    }
  }, [selectedCategories]);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('information_hub_posts')
        .select(`
          category:category_id(
            id,
            name
          )
        `)
        .not('category_id', 'is', null);

      if (error) throw error;

      const uniqueCategories = data.reduce((acc: any[], post) => {
        if (post.category && !acc.find(c => c.id === post.category.id)) {
          acc.push(post.category);
        }
        return acc;
      }, []);

      setCategories(uniqueCategories.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err: any) {
      console.error('Error fetching categories:', err);
      setError(err.message);
    }
  };

  const fetchInfoHubPosts = async () => {
    try {
      let query = supabase
        .from('information_hub_posts')
        .select(`
          *,
          author:author_id(
            id,
            full_name,
            display_name,
            verified,
            avatar_url
          ),
          category:category_id(
            id,
            name
          )
        `)
        .order('created_at', { ascending: false });

      if (selectedCategories.length > 0) {
        query = query.in('category_id', selectedCategories);
      }

      const { data, error } = await query;

      if (error) throw error;
      setInfoHubPosts(data || []);
    } catch (err: any) {
      console.error('Error fetching info hub posts:', err);
      setError(err.message);
    }
  };

  const handleEventUpdate = async () => {
    if (!user) return;

    try {
      const { data: memberships } = await supabase
        .from('community_members')
        .select('community_id')
        .eq('user_id', user.id);

      const communityIds = memberships?.map(m => m.community_id) || [];
      if (communityIds.length > 0) {
        await fetchEvents(communityIds);
      }
    } catch (err) {
      console.error('Error updating events:', err);
    }
  };

  if (!user) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-4">
        <h1 className="font-dillan text-4xl font-bold text-primary mb-4 text-center">
          Welcome to MusicNet
        </h1>
        <p className="font-dillan text-xl text-neutral max-w-2xl mx-auto text-center mb-8">
          Connect with music industry professionals, join communities, and discover opportunities.
        </p>
        <Button variant="primary" size="lg" as="a" href="/auth">
          Get Started
        </Button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <Card>
          <div className="p-6 text-center">
            <p className="text-secondary-dark">{error}</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 pb-20">
      <div className="flex flex-wrap justify-center sm:justify-between items-center gap-2 mb-6">
        <div className="flex flex-wrap justify-center bg-surface rounded-xl p-1 gap-2 sm:gap-4">
          <button
            onClick={() => setActiveView('community')}
            className={`w-40 py-2 text-sm sm:text-sm rounded-full transition-colors font-dillan uppercase ${
              activeView === 'community'
                ? 'bg-secondary-light text-primary border-4 border-secondary-light'
                : 'text-neutral border-4 border-secondary-light hover:text-primary'
            }`}
          >
            Community Posts
          </button>
          
          <button
            onClick={() => setActiveView('info')}
            className={`w-40 py-2 text-sm sm:text-sm rounded-full transition-colors font-dillan uppercase ${
              activeView === 'info'
                ? 'bg-secondary-light text-primary border-4 border-secondary-light'
                : 'text-neutral border-4 border-secondary-light hover:text-primary'
            }`}
          >
            Information Hub
          </button>
        </div>

        {activeView === 'community' && (
          <select
            value={feedType}
            onChange={(e) => setFeedType(e.target.value as 'posts' | 'events')}
            className="w-auto px-3 py-2 rounded-full bg-surface text-neutral font-dillan focus:outline-none transition-colors"
          >
            <option value="posts">All Posts</option>
            <option value="events">Events</option>
          </select>
        )}

        {activeView === 'info' && (
          <select
            value={selectedCategories[0] || 'all'}
            onChange={handleCategoryChange}
            className="w-auto px-3 py-2 rounded-full bg-surface text-neutral font-dillan focus:outline-none transition-colors"
          >
            <option value="all">All Categories</option>
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
        <div>
          {activeView === 'community' ? (
            hasCommunities ? (
              <div className="space-y-4">
                {feedType === 'posts' ? (
                  posts.map((post) => (
                    <PostCard
                      key={post.id}
                      post={post}
                      onReplyClick={() => setSelectedPost(post)}
                      onUpdate={handlePostUpdate}
                    />
                  ))
                ) : (
                  events.map((event) => (
                    <EventCard
                      key={event.id}
                      event={event}
                      onUpdate={handleEventUpdate}
                    />
                  ))
                )}
              </div>
            ) : (
              <Card>
                <div className="p-6 text-center">
                  <Users className="w-12 h-12 text-neutral mx-auto mb-4" />
                  <h2 className="text-xl font-bold font-dillan text-primary mb-2">
                    Your feed is empty
                  </h2>
                  <p className="text-neutral font-dillan mb-6">
                    Join communities to see posts from other music professionals.
                  </p>
                  <Button variant="primary" className="w-64 py-2 text-m bg-neutral text-surface-light border-4 border-neutral hover:text-surface-light hover:bg-primary-dark hover:border-primary-dark mx-auto flex items-center justify-center" as="a" href="/communities">
                    Browse Communities
                  </Button>
                </div>
              </Card>
            )
          ) : (
            <InformationHubList posts={infoHubPosts} />
          )}
        </div>
      </div>

      {selectedPost && (
        <PostDetailsModal
          post={selectedPost}
          onClose={() => setSelectedPost(null)}
          onUpdate={handlePostUpdate}
        />
      )}
    </div>
  );
}

export default Home;